import { ServiceMode, parseServiceMode } from '../core/types/service-mode.js';

export interface SessionConnectResponse {
  livekitToken: string;
  livekitUrl: string;
  roomName: string;
  /**
   * Business session id, for correlating a console with the session inspector.
   * The server has always sent it; empty string if an older server omits it.
   * Diagnostic only — never a substitute for `livekitToken`.
   */
  sessionId: string;
  serviceMode: ServiceMode;
  dealerMuted: boolean;
}

/**
 * Non-OK response from `/api/sessions/connect`, carrying the server's
 * structured verdict alongside the human message: `code` is the API body's
 * `error` field (e.g. `AUTH_INVALID_SESSION_TOKEN`), `status` the HTTP status.
 */
export class SessionConnectError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly status: number
  ) {
    super(message);
    this.name = 'SessionConnectError';
  }
}

/** `/connect` verdicts that mean the business session is over — no retry can succeed. */
const TERMINAL_SESSION_CODES: ReadonlySet<string> = new Set(['AUTH_INVALID_SESSION_TOKEN']);

/**
 * True when a `/connect` failure is the server's terminal verdict on the
 * session (closed, swept, or expired — the token record is gone). A bare 401
 * counts too: `/connect` authenticates by session token alone, so a 401 with
 * an unparsable body can only mean the token is dead.
 */
export function isTerminalSessionError(error: unknown): boolean {
  if (!(error instanceof SessionConnectError)) return false;
  return (error.code !== null && TERMINAL_SESSION_CODES.has(error.code)) || error.status === 401;
}

/**
 * Session connect for the SDK (and, via IframeSDKService, the iframe app):
 * calls the API server's `/api/sessions/connect` endpoint, parses the flags
 * payload, and returns everything needed to open a LiveKit room.
 *
 * Throws on network failure or non-OK response (`SessionConnectError`). Caller
 * (DealerSDK.connect) surfaces these as rejected Promises per the SDK error policy.
 */
export async function fetchSession(
  serviceUrl: string,
  sessionToken: string
): Promise<SessionConnectResponse> {
  const response = await fetch(`${serviceUrl}/api/sessions/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionToken }),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => null)) as {
      error?: unknown;
      message?: unknown;
    } | null;
    const code =
      typeof errorData?.error === 'string' && errorData.error.length > 0 ? errorData.error : null;
    const message =
      typeof errorData?.message === 'string' && errorData.message.length > 0
        ? errorData.message
        : `Failed to connect to session (HTTP ${response.status})`;
    throw new SessionConnectError(message, code, response.status);
  }

  const data = (await response.json()) as {
    livekitToken?: unknown;
    livekitUrl?: unknown;
    roomName?: unknown;
    sessionId?: unknown;
    flags?: unknown;
  };

  if (typeof data.livekitToken !== 'string' || typeof data.livekitUrl !== 'string') {
    throw new Error('Invalid session/connect response: missing livekitToken or livekitUrl');
  }

  return {
    livekitToken: data.livekitToken,
    livekitUrl: data.livekitUrl,
    roomName: typeof data.roomName === 'string' ? data.roomName : '',
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : '',
    serviceMode: extractMode(data.flags),
    dealerMuted: extractMuted(data.flags),
  };
}

function extractMode(raw: unknown): ServiceMode {
  if (!raw || typeof raw !== 'object') return ServiceMode.Normal;
  const mode = (raw as Record<string, unknown>).mode;
  try {
    return parseServiceMode(mode);
  } catch {
    return ServiceMode.Normal;
  }
}

function extractMuted(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  return Boolean((raw as Record<string, unknown>).muted);
}
